import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const PRIO = { critical: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", medium: "bg-yellow-100 text-yellow-700", low: "bg-slate-100 text-slate-600" };
const STAT = { open: "bg-blue-100 text-blue-700", in_progress: "bg-indigo-100 text-indigo-700", resolved: "bg-emerald-100 text-emerald-700" };

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold font-display flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-indigo-500" />Support</h1><p className="text-sm text-slate-500">Raise a ticket and chat with the PK AI team.</p></div>
        <Button onClick={() => setShowNew(true)} data-testid="support-new-btn"><Plus className="w-4 h-4 mr-1" />New Ticket</Button>
      </div>
      <Card className="border shadow-none overflow-x-auto">
        {!tickets ? <Loader /> : tickets.length === 0 ? <div className="p-10 text-center text-sm text-slate-400" data-testid="support-empty">No tickets yet. Raise one and we'll help you out.</div> :
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Ticket</th><th className="px-4 py-2">Subject</th><th className="px-4 py-2">Priority</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Updated</th></tr></thead>
            <tbody>{tickets.map((t) => (
              <tr key={t.id} className="border-b hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer" onClick={() => setOpenId(t.id)} data-testid={`support-row-${t.id}`}>
                <td className="px-4 py-3 font-medium">{t.ticket_no}</td><td className="px-4 py-3">{t.subject}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${PRIO[t.priority]}`}>{t.priority}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STAT[t.status]}`}>{t.status.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-slate-400">{(t.updated_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}</tbody></table>}
      </Card>
      {openId && <TicketThread id={openId} onClose={() => { setOpenId(null); load(); }} />}
      <NewTicket open={showNew} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function TicketThread({ id, onClose }) {
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const send = async () => { if (!msg.trim()) return; await api.post(`/tickets/${id}/message`, { text: msg }); setMsg(""); load(); };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="support-thread">
      {!t ? <Loader /> : <>
        <DialogHeader><DialogTitle>{t.ticket_no} · {t.subject}</DialogTitle></DialogHeader>
        <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-3">
          {(t.messages || []).map((m, i) => (
            <div key={i} className={`flex ${m.from === "client" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.from === "client" ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}><div className="text-[10px] opacity-70 mb-0.5">{m.from === "client" ? "You" : "PK AI Support"} · {(m.at || "").slice(0, 16).replace("T", " ")}</div>{m.text}</div>
            </div>
          ))}
        </div>
        {t.status !== "resolved" ? <div className="flex gap-2"><Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a reply..." data-testid="support-reply" /><Button onClick={send} data-testid="support-send"><Send className="w-4 h-4" /></Button></div>
          : <div className="text-sm text-emerald-600 text-center">This ticket is resolved. Raise a new one if you need more help.</div>}
      </>}
    </DialogContent></Dialog>
  );
}

function NewTicket({ open, onClose, onDone }) {
  const [f, setF] = useState({ subject: "", priority: "medium", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject || !f.message) { toast.error("Subject and message required"); return; }
    setBusy(true);
    try { await api.post("/tickets", f); toast.success("Ticket raised — we'll respond soon"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent data-testid="support-new-dialog">
      <DialogHeader><DialogTitle>Raise a Support Ticket</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="support-subject" /></div>
        <div><Label>Priority</Label><Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["critical", "high", "medium", "low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>How can we help?</Label><Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="support-message" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="support-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function Loader() { return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>; }
